/**
 * The orders a technician opened, kept on their device (orders overhaul
 * Phase 4 — the offline mode). Each time the order page loads an order for
 * someone who works orders without editing them (the work view; the server
 * leaves every dollar amount out of what they get), its details are kept in
 * the browser's IndexedDB — and its inspections, when the Inspections tab
 * loads them (keepInspectionsCopy). With no signal the page shows that copy,
 * saying when it was kept (hooks/useOrderDetailApi.js, InspectionsTab.js),
 * and their saves wait in the outbox (utils/offlineOutbox.js).
 *
 * Only the signed-in person's copies are read (setOfflineOrdersOwner, from
 * AdminLayout); signing out deletes theirs (AuthContext). The newest 25 are
 * kept, none older than 14 days. An order refreshing often (every save, every
 * update from someone else) is written at most every 15 seconds — the latest
 * each time — and the old ones are found by their keys alone (an index on
 * when they were kept), never by reading the copies back. Without IndexedDB
 * nothing is kept.
 */

const DB_NAME = "chariot-offline-orders";
const STORE = "orders";
const MAX_COPIES = 25;
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const WRITE_EVERY_MS = 15000;
const BY_AGE = "savedAt";

let owner = null;
let keeping = false;
const lastWrite = new Map(); // key → when it was last written
const later = new Map(); // key → { data, timer } written when its 15 s are up

let dbPromise = null;
function openDb() {
  if (!dbPromise) {
    const opening = new Promise((resolve) => {
      try {
        if (typeof indexedDB === "undefined") { resolve(null); return; }
        const req = indexedDB.open(DB_NAME, 2);
        req.onupgradeneeded = () => {
          const store = req.result.objectStoreNames.contains(STORE)
            ? req.transaction.objectStore(STORE)
            : req.result.createObjectStore(STORE, { keyPath: "key" });
          if (!store.indexNames.contains(BY_AGE)) store.createIndex(BY_AGE, "savedAt");
        };
        req.onsuccess = () => {
          const db = req.result;
          const lose = () => { if (dbPromise === opening) dbPromise = null; };
          db.onclose = lose;
          db.onversionchange = () => { try { db.close(); } catch { /* fine */ } lose(); };
          resolve(db);
        };
        const failed = () => { if (dbPromise === opening) dbPromise = null; resolve(null); };
        req.onerror = failed;
        req.onblocked = failed;
      } catch {
        resolve(null);
      }
    });
    dbPromise = opening;
  }
  return dbPromise;
}

async function idb(mode, run) {
  const db = await openDb();
  if (!db) return { ok: false };
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const req = run(tx.objectStore(STORE));
      tx.oncomplete = () => resolve({ ok: true, value: req ? req.result : undefined });
      tx.onerror = () => resolve({ ok: false });
      tx.onabort = () => resolve({ ok: false });
    } catch {
      resolve({ ok: false });
    }
  });
}

/** Whose copies, and whether to keep them (someone in the work view). */
export function setOfflineOrdersOwner(userId, keep) {
  owner = userId ? String(userId) : null;
  keeping = !!(owner && keep);
}

// What a copy is of: the order's details (key "<who>:<order>"), or another
// part of the order page kept beside it ("<who>:<order>:<part>" — its
// inspections). Each part keeps its own newest MAX_COPIES.
const keyOf = (who, orderId, part) => `${who}:${orderId}${part ? `:${part}` : ""}`;
const partOf = (key) => String(key).split(":")[2] || "";

async function write(who, orderId, data, part = "") {
  const key = keyOf(who, orderId, part);
  lastWrite.set(key, Date.now());
  await idb("readwrite", (st) => st.put({ key, owner: who, orderId: String(orderId), savedAt: Date.now(), data }));
  // The newest MAX_COPIES of theirs, none too old — by key and age alone.
  const keys = await idb("readonly", (st) => st.index(BY_AGE).getAllKeys()); // oldest first
  if (!keys.ok || !Array.isArray(keys.value)) return;
  const theirs = keys.value.filter((k) => String(k).startsWith(`${who}:`) && partOf(k) === part);
  const tooMany = theirs.slice(0, Math.max(0, theirs.length - MAX_COPIES));
  const tooOld = await idb("readonly", (st) => st.index(BY_AGE).getAllKeys(IDBKeyRange.upperBound(Date.now() - MAX_AGE_MS)));
  const stale = new Set([...tooMany, ...(tooOld.ok && Array.isArray(tooOld.value) ? tooOld.value : [])]);
  if (stale.size) await idb("readwrite", (st) => { stale.forEach((k) => st.delete(k)); return null; });
}

/**
 * The order's inspections as last loaded (the Inspections tab), for no
 * signal — without their photos' links (they expire within the hour; the
 * photos come back with signal). A no-op for anyone not keeping copies.
 */
export async function keepInspectionsCopy(orderId, inspections) {
  if (!keeping || !orderId || !Array.isArray(inspections)) return;
  const bare = inspections.map((insp) => ({
    ...insp,
    items: (insp.items || []).map(({ media, media_loaded_at, ...it }) => it), // eslint-disable-line no-unused-vars
  }));
  await keepThrottled(orderId, bare, "inspections");
}

/** This order's inspections as this device kept them: { data, savedAt } or null. */
export async function readInspectionsCopy(orderId) {
  if (!owner || !orderId) return null;
  const got = await idb("readonly", (st) => st.get(keyOf(owner, orderId, "inspections")));
  const row = got.ok ? got.value : null;
  if (!row || Date.now() - row.savedAt > MAX_AGE_MS) return null;
  return { data: row.data, savedAt: row.savedAt };
}

/** Keeps this order's details (a no-op for anyone not keeping copies). */
export async function keepOrderCopy(orderId, data) {
  if (!keeping || !orderId || !data) return;
  await keepThrottled(orderId, data, "");
}

// At most every 15 s per copy: the latest goes in when its 15 s are up.
async function keepThrottled(orderId, data, part) {
  const who = owner;
  const key = keyOf(who, orderId, part);
  const since = Date.now() - (lastWrite.get(key) || 0);
  if (since >= WRITE_EVERY_MS && !later.has(key)) {
    await write(who, orderId, data, part);
    return;
  }
  // Written a moment ago: the latest goes in when its 15 s are up.
  const waiting = later.get(key);
  if (waiting) { waiting.data = data; return; }
  const entry = { data, timer: null };
  entry.timer = setTimeout(() => {
    later.delete(key);
    if (keeping && owner === who) write(who, orderId, entry.data, part);
  }, Math.max(0, WRITE_EVERY_MS - since));
  later.set(key, entry);
}

/** This order's copy for the signed-in person: { data, savedAt } or null. */
export async function readOrderCopy(orderId) {
  if (!owner || !orderId) return null;
  const got = await idb("readonly", (st) => st.get(`${owner}:${orderId}`));
  const row = got.ok ? got.value : null;
  if (!row || Date.now() - row.savedAt > MAX_AGE_MS) return null;
  return { data: row.data, savedAt: row.savedAt };
}

/** Deletes a person's copies (signing out). */
export async function forgetOrderCopies(userId) {
  if (!userId) return;
  for (const [key, entry] of later) {
    if (key.startsWith(`${userId}:`)) { clearTimeout(entry.timer); later.delete(key); }
  }
  const all = await idb("readonly", (st) => st.getAll());
  if (!all.ok || !Array.isArray(all.value)) return;
  const theirs = all.value.filter((r) => r.owner === String(userId));
  if (theirs.length) await idb("readwrite", (st) => { theirs.forEach((r) => st.delete(r.key)); return null; });
}

/** No signal (as opposed to Chariot refusing): a fetch that threw or timed out, or the device says it's offline. */
export const isNoSignal = (err) => (typeof navigator !== "undefined" && navigator.onLine === false)
  || err?.name === "TypeError" || err?.name === "AbortError" || err?.name === "TimeoutError";
